import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Loading } from '../../components/Loading';
import { selectUser } from '../auth/authSlice';
import {
  useGetCertificationByIdMutation,
  useGetQuizLogMutation,
  useLoadEnrollQuery,
} from '../exam/certificationApiSlice';
import { statusTranslate } from '../exam/helpers/statusTranslate';

const PASSING_SCORE_PERCENT = 70;

function getResultMeta(approved: boolean, pending: boolean) {
  if (approved) {
    return {
      badgeClassName:
        'border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-100',
      panelClassName:
        'border-emerald-200 bg-gradient-to-br from-white via-emerald-50 to-emerald-100',
      progressClassName: 'bg-emerald-500',
      title: 'Aprovado',
      description:
        'Seu desempenho atingiu a média necessária para concluir esta certificação.',
    };
  }

  if (pending) {
    return {
      badgeClassName:
        'border-amber-200 bg-amber-50 text-amber-700 shadow-sm shadow-amber-100',
      panelClassName:
        'border-amber-200 bg-gradient-to-br from-white via-amber-50 to-amber-100',
      progressClassName: 'bg-amber-500',
      title: 'Em aprovação',
      description:
        'A prova foi concluída e está em processo de aprovação. O resultado será disponibilizado em breve.',
    };
  }

  return {
    badgeClassName:
      'border-rose-200 bg-rose-50 text-rose-700 shadow-sm shadow-rose-100',
    panelClassName:
      'border-rose-200 bg-gradient-to-br from-white via-rose-50 to-rose-100',
    progressClassName: 'bg-rose-500',
    title: 'Não aprovado',
    description:
      'A prova foi concluída, mas o resultado final não atingiu a média mínima exigida.',
  };
}

export function Result() {
  const user = useSelector(selectUser);
  const params = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useLoadEnrollQuery(
    { _id: params?.id || '' },
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    },
  );

  const [getQuizLog, { data: quizLog }] = useGetQuizLogMutation();
  const [getCertification, { data: certificationItem }] =
    useGetCertificationByIdMutation();

  useEffect(() => {
    const status = window.localStorage.getItem('certifica-updated');

    if (status !== 'true') {
      window.localStorage.setItem('certifica-updated', 'true');
      document.location.reload();
    }

    if (data?.[0].certification) {
      getCertification(data[0].certification);
      getQuizLog(data[0].quiz_log);
    }
  }, [data, getCertification, getQuizLog]);

  const certificationLog = data?.[0];
  const quizLogItem = quizLog?.[0];

  const resultStatusLabel = useMemo(() => {
    if (!certificationLog) return '';
    return certificationLog.approved
      ? 'Aprovado'
      : statusTranslate[certificationLog.status] ?? 'Reprovado';
  }, [certificationLog]);

  const percentScore = useMemo(() => {
    if (!quizLogItem) return 0;
    return Math.round(Number(quizLogItem.percent || 0) * 100);
  }, [quizLogItem]);

  const isPending = certificationLog?.status === '3';
  const resultMeta = getResultMeta(
    Boolean(certificationLog?.approved),
    isPending,
  );

  if (isLoading || !certificationLog || !quizLogItem) {
    return <Loading />;
  }

  const totalQuestions = Number(quizLogItem.questions || 0);
  const correctAnswers = Number(quizLogItem.questionsGrade || 0);
  const requiredCorrectAnswers = Math.ceil(
    (totalQuestions * PASSING_SCORE_PERCENT) / 100,
  );
  const missingCorrectAnswers = Math.max(
    requiredCorrectAnswers - correctAnswers,
    0,
  );
  const normalizedScore = Math.min(Math.max(percentScore, 0), 100);
  const finishedAtLabel = certificationLog.finished?.$date
    ? new Date(certificationLog.finished.$date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--';
  let performanceSummary = `Faltaram ${missingCorrectAnswers} acerto${
    missingCorrectAnswers === 1 ? '' : 's'
  } para alcançar ${PASSING_SCORE_PERCENT}%.`;
  if (certificationLog.approved) {
    performanceSummary = `Você superou a média mínima de ${PASSING_SCORE_PERCENT}% para aprovação.`;
  } else if (isPending) {
    performanceSummary =
      'O resultado será divulgado após a conclusão da avaliação.';
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-10">
      <div className="mx-auto w-full max-w-[1120px] px-4 py-4 xl:px-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                Resultado final
              </p>
              <h1 className="mt-1 text-2xl font-semibold leading-tight tracking-[-0.02em] text-slate-900 sm:text-3xl">
                {certificationItem?.[0].title}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Visualize seu status e desempenho em um resumo único.
              </p>
            </div>

            <div className="flex shrink-0 flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <span
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${resultMeta.badgeClassName}`}
              >
                {resultStatusLabel}
              </span>
              <button
                type="button"
                onClick={() => navigate('/my-certifications')}
                className="cta-guidance cta-guidance-blue rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
              >
                Voltar para Minhas inscrições
              </button>
            </div>
          </div>
        </section>

        <section
          className={`mt-4 rounded-3xl border p-6 shadow-sm ${resultMeta.panelClassName}`}
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Status da prova
              </p>
              <h2 className="mt-2 text-3xl font-semibold text-slate-900">
                {resultMeta.title}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-700">
                {resultMeta.description}
              </p>
            </div>

            <div className="w-full max-w-[280px] rounded-2xl border border-white/70 bg-white/80 px-5 py-4 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Nota final
              </p>
              <p className="mt-2 text-4xl font-semibold tracking-[-0.04em] text-slate-900">
                {percentScore}%
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {correctAnswers} de {totalQuestions} questões corretas
              </p>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              <span>Média mínima</span>
              <span>{PASSING_SCORE_PERCENT}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/80">
              <div
                className={`h-full rounded-full transition-all ${resultMeta.progressClassName}`}
                style={{ width: `${normalizedScore}%` }}
              />
            </div>
            <p className="mt-3 text-sm font-medium text-slate-700">
              {performanceSummary}
            </p>
          </div>
        </section>

        <section className="mt-4 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Acertos
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {correctAnswers}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Questões
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {totalQuestions}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              Finalizada em
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              {finishedAtLabel}
            </p>
          </div>
        </section>

        <section className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Candidato
              </p>
              <p className="mt-1.5 text-lg font-semibold text-slate-900">
                {user?.name}
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Status técnico
              </p>
              <p className="mt-1.5 text-lg font-semibold text-slate-900">
                {statusTranslate[certificationLog.status] ?? resultStatusLabel}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
